import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Image as ImageIcon, Save, Trash2 } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { resolveImageUrl } from "@/lib/images";

export default function BlogEditorPage() {
  const { id, blogId } = useParams();
  const isEdit = Boolean(blogId);
  // Admin editing an editorial post (no listing context in the URL)
  const isAdminEdit = isEdit && !id;
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [heroUrl, setHeroUrl] = useState("");
  const [tags, setTags] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [original, setOriginal] = useState(null);

  useEffect(() => {
    if (!isEdit) return;
    if (isAdminEdit) {
      // Admin path — fetch full post by id
      api.get(`/admin/blog/${blogId}`).then((r) => {
        const p = r.data;
        setOriginal(p);
        setTitle(p.title || "");
        setHeroUrl(p.hero_url || "");
        setTags((p.tags || []).join(", "));
        setBodyHtml(p.body_html || "");
      });
      return;
    }
    api.get("/blog", { params: { listing_id: id, page_size: 100 } }).then((r) => {
      const p = (r.data.results || []).find((x) => x.id === blogId);
      if (!p) return;
      setOriginal(p);
      setTitle(p.title);
      setHeroUrl(p.hero_url || "");
      setTags((p.tags || []).join(", "));
      // For edit, also fetch full content (body_html) via the public slug endpoint
      api.get(`/blog/${p.slug}`).then((rr) => setBodyHtml(rr.data.body_html || ""));
    });
  }, [isEdit, isAdminEdit, blogId, id]);

  const insertImage = (url) => {
    const html = `\n<p><img src="${resolveImageUrl(url)}" alt="" /></p>\n`;
    setBodyHtml((b) => b + html);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEdit && original) {
        await api.patch(`/blog/${blogId}`, {
          title,
          body_html: bodyHtml,
          hero_url: heroUrl || null,
          tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        });
        toast.success("Updated");
      } else {
        await api.post("/blog", {
          listing_id: id,
          title,
          body_html: bodyHtml,
          hero_url: heroUrl || null,
          tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        });
        toast.success("Published");
      }
      navigate(isAdminEdit ? "/admin/blog" : `/dashboard/listings/${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const del = async () => {
    if (!isEdit) return;
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/blog/${blogId}`);
    toast.success("Deleted");
    navigate(isAdminEdit ? "/admin/blog" : `/dashboard/listings/${id}`);
  };

  const backHref = isAdminEdit ? "/admin/blog" : `/dashboard/listings/${id}`;

  return (
    <div className="container-page py-10" data-testid="blog-editor-page">
      <Link to={backHref} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>
      <h1 className="mt-4 font-display text-4xl font-bold text-primary">{isEdit ? "Edit post" : "New blog post"}</h1>

      <form onSubmit={submit} className="mt-8 grid max-w-3xl gap-5" data-testid="blog-form">
        <label>
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Title</span>
          <input
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-md border border-border bg-card px-3 py-2.5 text-lg font-semibold outline-none focus:ring-2 focus:ring-accent"
            data-testid="blog-title"
          />
        </label>

        <div>
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Hero image</span>
          <div className="flex items-center gap-4">
            <div className="grid h-20 w-32 place-items-center overflow-hidden rounded-md border border-border bg-muted">
              {heroUrl ? <img src={resolveImageUrl(heroUrl)} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
            </div>
            <ImageUpload label="Upload hero" testId="blog-hero" onUploaded={(url) => setHeroUrl(url)} />
            {heroUrl && (
              <button type="button" onClick={() => setHeroUrl("")} className="text-sm text-muted-foreground hover:text-destructive">Remove</button>
            )}
          </div>
        </div>

        <label>
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Tags (comma separated)</span>
          <input value={tags} onChange={(e) => setTags(e.target.value)} className="w-full rounded-md border border-border bg-card px-3 py-2.5 outline-none focus:ring-2 focus:ring-accent" data-testid="blog-tags" placeholder="wellness, community, mental health" />
        </label>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="block text-xs font-semibold uppercase tracking-wider text-foreground/70">Body (HTML supported)</span>
            <ImageUpload label="Insert image" testId="blog-inline-image" onUploaded={insertImage} />
          </div>
          <textarea
            required
            rows={16}
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            className="w-full rounded-md border border-border bg-card px-3 py-2.5 font-mono text-sm outline-none focus:ring-2 focus:ring-accent"
            placeholder="<p>Write your post here. You can use simple HTML like &lt;p&gt;, &lt;h2&gt;, &lt;a&gt;, &lt;ul&gt;…</p>"
            data-testid="blog-body"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" disabled={submitting} className="btn-primary" data-testid="blog-publish">
            <Save className="h-4 w-4" /> {isEdit ? "Save" : "Publish"}
          </button>
          {isEdit && (
            <button type="button" onClick={del} className="text-sm font-semibold text-destructive hover:underline" data-testid="blog-delete">
              <Trash2 className="-mb-0.5 mr-1 inline h-4 w-4" /> Delete
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
